'use client'
import { useCallbackRef, useDebouncedCallback } from '@mantine/hooks'
import { notifyUser } from 'lib/form'
import { FormContext, type FormContextType } from 'lib/formContext'
import { useCloseModal } from 'lib/modalContext'
import { notification } from 'lib/notification'
import {
  type ErrorResponse,
  type ServerActionResponse,
} from 'lib/serverActionsResponse'
import { useRouter } from 'next/navigation'
import {
  type ReactNode,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'

export type FormAction<Body = unknown> = (
  _: any,
  formData: FormData,
) => Promise<ServerActionResponse<Body>>

interface Props<Body = unknown> {
  action: FormAction<Body>
  children: ReactNode
  className?: string
  autoSubmitAction?: FormAction
  onChange?: () => void
  onSuccess?: (body?: Body) => void
  onError?: (response: ErrorResponse) => void
  ref?: React.RefObject<HTMLFormElement>
  serverValidation?: boolean
}
export function Form<Body = unknown>({
  action,
  children,
  className,
  autoSubmitAction,
  onSuccess,
  onError,
  onChange,
  ref: extRef,
  serverValidation,
}: Props<Body>) {
  const closeDrawer = useCloseModal()
  const router = useRouter()
  const localRef = useRef<HTMLFormElement>(null)
  const ref = extRef || localRef
  const onSuccessRef = useCallbackRef(onSuccess)
  const onErrorRef = useCallbackRef(onError)
  const [validity, setValidity] = useState<Record<string, string>>({})
  const [submitCount, setSubmitCount] = useState(0)
  const pendingUploadsRef = useRef(0)
  const uploadResolverRef = useRef<(() => void) | null>(null)

  const cancelAutoReset = useCallback(
    (event: Event) => {
      const form = ref.current
      if (!form) return
      if (event.target !== form) return
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    [ref],
  )

  // Drop a leftover listener if the form goes away before React resets it.
  useEffect(() => {
    const doc = ref.current?.ownerDocument
    if (!doc) return
    return () => doc.removeEventListener('reset', cancelAutoReset, true)
  }, [ref, cancelAutoReset])

  // React resets an uncontrolled form as soon as a function `action` resolves.
  // Our forms autosave and stay on screen after submitting, so that reset only
  // does damage: Radix form controls (>= @radix-ui/react-radio-group 1.4.3,
  // and likewise Select and Switch) answer the reset by restoring the value
  // they had on first render and report it through `onValueChange`, which looks
  // like user input — it clears the user's selection, invalidates the form and
  // kicks off an autosave that writes the reverted value back to the server.
  // So we cancel that one reset, and only that one: the listener lives just
  // long enough for React to perform it. Resets the user asks for
  // (`<button type="reset">`) happen while no listener is attached.
  // It has to be a *capture* listener on the document: `reset` bubbles, so a
  // bubble-phase listener here would only run after the event had already
  // reached the form — and Radix attaches its listener to the form element
  // itself, so it would revert before we ever got to cancel.
  const cancelNextAutoReset = useCallback(() => {
    const form = ref.current
    if (!form) return
    const doc = form.ownerDocument
    doc.addEventListener('reset', cancelAutoReset, {
      once: true,
      capture: true,
    })
  }, [ref, cancelAutoReset])

  const submit = useCallback(
    async (formData: FormData) => {
      if (pendingUploadsRef.current > 0) {
        notification.info({
          title: 'Upload läuft',
          body: 'Das Formular wird abgesendet, sobald der Vorgang abgeschlossen ist.',
        })
        if (uploadResolverRef.current) return
        await new Promise<void>((resolve) => {
          uploadResolverRef.current = resolve
        })
        uploadResolverRef.current = null
        formData = new FormData(ref.current!)
      }

      const response = await action(
        { status: 'pending', message: 'Pending' },
        formData,
      )
      notifyUser(response)
      if (response.status === 'error') {
        onErrorRef?.(response)
      }
      if (response.status === 'success' || response.status === 'info') {
        if ('redirect' in response && response.redirect) {
          router.push(response.redirect)
        }
        closeDrawer()
        onSuccessRef?.(response.body)
      }
      if (serverValidation && response.status === 'invalid') {
        const errors: Record<string, string> = {}
        for (const issue of response.issues) {
          const [root, ...rest] = issue.path
          const key = `${String(root ?? '')}${rest.map((part) => `[${String(part)}]`).join('')}`
          errors[key] = [errors[key], issue.message].filter(Boolean).join('\n')
        }
        flushSync(() => setValidity(errors))
        const controls = Array.from(ref.current?.elements ?? [])
        for (const control of controls) {
          if (
            !(
              control instanceof HTMLInputElement ||
              control instanceof HTMLSelectElement ||
              control instanceof HTMLTextAreaElement
            )
          )
            continue
          if (control instanceof HTMLInputElement && control.type === 'hidden')
            continue
          if (control.disabled) continue
          if (
            Object.keys(errors).some(
              (key) =>
                key === control.name || key.startsWith(`${control.name}[`),
            )
          ) {
            control.focus()
            break
          }
        }
      }
      // Force component to re-render until
      // https://github.com/facebook/react/issues/31695 is fixed
      setSubmitCount((prev) => prev + 1)
    },
    [
      action,
      router,
      onSuccessRef,
      onErrorRef,
      closeDrawer,
      serverValidation,
      ref,
    ],
  )

  const formAction = useCallback(
    async (formData: FormData) => {
      try {
        await submit(formData)
      } finally {
        // React runs its automatic reset once this action resolves.
        cancelNextAutoReset()
      }
    },
    [submit, cancelNextAutoReset],
  )

  const debouncedSubmit = useDebouncedCallback(
    () =>
      autoSubmitAction &&
      startTransition(async () => {
        if (!ref.current) return
        const response = await autoSubmitAction(
          { status: 'pending', message: 'Pending' },
          new FormData(ref.current),
        )
        notifyUser(response)
      }),
    500,
  )

  const startUpload = useCallback(() => {
    pendingUploadsRef.current++

    return () => {
      pendingUploadsRef.current--
      if (pendingUploadsRef.current === 0) {
        uploadResolverRef.current?.()
      }
    }
  }, [])

  const contextValue = useMemo(
    (): FormContextType => ({
      onChange: (field) => {
        onChange?.()
        setValidity((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(
              ([key]) => key !== field && !key.startsWith(`${field}[`),
            ),
          ),
        )
        debouncedSubmit()
      },
      submitCount,
      validity,
      startUpload,
    }),
    [debouncedSubmit, validity, onChange, submitCount, startUpload],
  )

  return (
    <form
      ref={ref}
      action={formAction}
      className={className}
      onSubmitCapture={async (event) => {
        if (pendingUploadsRef.current === 0) return
        event.preventDefault()
        event.stopPropagation()
        if (uploadResolverRef.current) return
        const form = event.currentTarget
        const submitter = (event.nativeEvent as SubmitEvent).submitter
        notification.info({
          title: 'Upload läuft',
          body: 'Das Formular wird abgesendet, sobald der Vorgang abgeschlossen ist.',
        })
        await new Promise<void>((resolve) => {
          uploadResolverRef.current = resolve
        })
        uploadResolverRef.current = null
        // Resume the normal submit event so creation headers commit completed drafts
        // only after their uploaded storage keys are available.
        if (form.isConnected)
          form.requestSubmit(
            submitter instanceof HTMLButtonElement && submitter.form === form
              ? submitter
              : undefined,
          )
      }}
      onInvalid={(event) => {
        const { name, validationMessage } = event.target as HTMLInputElement
        if (name && validationMessage) {
          setValidity((prev) => ({
            ...prev,
            [name]: validationMessage,
          }))
        }
      }}
    >
      <FormContext value={contextValue}>{children}</FormContext>
    </form>
  )
}
